import { useState } from "react";
import {
  Compass, PenTool, FileCheck,
  ChevronDown, ChevronRight, Check, Circle, Phone, Mail,
  Building2, AlertTriangle, Info, ExternalLink,
} from "lucide-react";
import InfoBentoCard from "@ui/InfoBentoCard";
import useFirestoreDoc from "@data/hooks/useFirestoreDoc";
import useKeepInPlace from "@shared/hooks/useKeepInPlace";
import {
  permitPhases, keyContacts, keyFacts, zoningDistricts, reviewPaths, stepOwners, stepsForPath,
} from "@domain/permits/saratogaGuidelines";

const iconMap = {
  Compass, PenTool, FileCheck,
};

function StepBadges({ item, reviewPath }) {
  const owner = stepOwners[item.owner];
  const conditional = item.onlyFor && reviewPath === "unknown";
  return (
    <div className="flex items-center gap-1.5 flex-wrap mt-1">
      {owner && (
        <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider ${owner.cls}`}>{owner.label}</span>
      )}
      {item.parallel && (
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-400 font-semibold">⇉ in parallel</span>
      )}
      {item.optional && (
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-500/15 text-gray-400">if applicable</span>
      )}
      {conditional && (
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">only for Administrative Design Review</span>
      )}
      {item.source && (
        <a
          href={item.source}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-0.5 text-[9px] text-indigo-400 hover:text-indigo-300"
          title="City source for this step"
        >
          <ExternalLink className="w-2.5 h-2.5" /> source
        </a>
      )}
    </div>
  );
}

export default function Permits() {
  const [checklist, setChecklist] = useFirestoreDoc("permits-checklist", {});
  const [permitSettings, setPermitSettings] = useFirestoreDoc("permit-settings", { reviewPath: "unknown" });
  const reviewPath = permitSettings?.reviewPath || "unknown";
  const [expanded, setExpanded] = useState(new Set(["planning"]));
  const [showZoning, setShowZoning] = useState(false);

  const keepInPlace = useKeepInPlace();
  const toggleExpand = (id, e) => {
    keepInPlace(e?.currentTarget);
    const next = new Set(expanded);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpanded(next);
  };

  const toggleItem = (itemId) => {
    setChecklist((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  // Only the steps that apply on this review path count toward progress
  // (old checklist ids that no longer exist are ignored).
  const phases = permitPhases.map((p) => ({ ...p, items: stepsForPath(p, reviewPath) }));
  const allSteps = phases.flatMap((p) => p.items);
  const totalItems = allSteps.length;
  const completedItems = allSteps.filter((i) => checklist[i.id]).length;
  const progressPct = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  // Find the next unchecked item across all phases (in order)
  let nextStep = null;
  let nextStepPhase = null;
  for (const phase of phases) {
    for (const item of phase.items) {
      if (!checklist[item.id]) {
        nextStep = item;
        nextStepPhase = phase;
        break;
      }
    }
    if (nextStep) break;
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Hero */}
      <InfoBentoCard
        cornerGlow
        icon={<Building2 className="w-4 h-4" />}
        accent="purple"
        title="Permits & Compliance"
        subtitle="City of Saratoga, CA — Residential Addition Requirements"
        className="mb-6"
      >
        <div className="flex items-center gap-2 flex-wrap mt-2">
          <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Review path</span>
          {reviewPaths.map((rp) => (
            <button
              key={rp.id}
              onClick={() => setPermitSettings((prev) => ({ ...prev, reviewPath: rp.id }))}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                reviewPath === rp.id ? "bg-indigo-600 text-white" : "text-gray-400 hover:text-gray-200"
              }`}
              style={reviewPath === rp.id ? undefined : { background: "var(--bg-input)", border: "1px solid var(--border-input)" }}
            >
              {rp.label}
            </button>
          ))}
        </div>
        {reviewPath === "unknown" && (
          <p className="text-[11px] text-gray-500 mt-1">
            Ask your planner which applies — steps marked "only for Administrative Design Review" (story poles, the City's 250-ft notice, appeal period) drop off on the Ministerial path.
          </p>
        )}
        <div className="flex items-center gap-4 md:gap-6 mt-2 flex-wrap">
          <div>
            <span className="text-lg font-bold text-purple-400">{progressPct}%</span>
            <span className="text-[10px] text-gray-500 ml-1.5">complete</span>
          </div>
          <div>
            <span className="text-lg font-bold text-emerald-400">{completedItems}</span>
            <span className="text-[10px] text-gray-500 ml-1.5">of {totalItems} items</span>
          </div>
          <div className="flex-1 min-w-[140px]">
            <div className="w-full rounded-full h-1.5" style={{ background: 'var(--bg-progress-track)' }}>
              <div
                className="bg-purple-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </div>

        {/* Next Step */}
        {nextStep ? (
          <div
            className="mt-3 p-3 rounded-lg flex items-start gap-3"
            style={{ background: "var(--bg-row-hover)", border: "1px solid var(--border-subtle)" }}
          >
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-amber-400 mb-0.5">
                Next Step — {nextStepPhase.name}
              </p>
              <p className="text-sm font-semibold" style={{ color: "var(--text-heading)" }}>
                {nextStep.text}
              </p>
              <StepBadges item={nextStep} reviewPath={reviewPath} />
              {nextStep.note && (
                <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
                  {nextStep.note}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div
            className="mt-3 p-3 rounded-lg flex items-center gap-3"
            style={{ background: "var(--bg-row-hover)", border: "1px solid var(--border-subtle)" }}
          >
            <Check className="w-4 h-4 text-emerald-400" />
            <p className="text-sm font-semibold text-emerald-400">All permit items completed!</p>
          </div>
        )}
      </InfoBentoCard>

      {/* Key Facts & Contacts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <InfoBentoCard cornerGlow frostedGradient accent="cyan" title="Key Information" className="h-full">
          <div className="space-y-1.5">
            {keyFacts.map((fact) => (
              <div key={fact.label} className="flex items-start gap-2 p-1.5 rounded" style={{ background: 'var(--bg-row-hover)' }}>
                <Info className="w-3 h-3 text-cyan-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{fact.label}</span>
                  <p className="text-xs text-gray-300">{fact.value}</p>
                </div>
              </div>
            ))}
          </div>
        </InfoBentoCard>

        <InfoBentoCard cornerGlow frostedGradient accent="amber" title="City Contacts" className="h-full">
          <div className="space-y-2">
            {keyContacts.map((contact) => (
              <div key={contact.dept} className="p-2 rounded-lg" style={{ background: 'var(--bg-row-hover)' }}>
                <p className="text-xs font-semibold text-gray-200 mb-1">{contact.dept}</p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1.5">
                    <Phone className="w-3 h-3 text-gray-500" />
                    <span className="text-[10px] text-gray-400">{contact.phone}</span>
                  </div>
                  {contact.email && (
                    <div className="flex items-center gap-1.5">
                      <Mail className="w-3 h-3 text-gray-500" />
                      <span className="text-[10px] text-gray-400">{contact.email}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </InfoBentoCard>
      </div>

      {/* Zoning Reference (collapsible) */}
      <InfoBentoCard cornerGlow frostedGradient accent="blue" className="mb-6">
        <div
          className="flex items-center justify-between cursor-pointer -m-4 p-4"
          onClick={(e) => { keepInPlace(e.currentTarget); setShowZoning(!showZoning); }}
        >
          <div className="flex items-center gap-2">
            {showZoning ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
            <span className="text-sm font-bold uppercase tracking-wider text-gray-200">Zoning District Reference</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400">R-1 Districts</span>
          </div>
        </div>
        {showZoning && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-row)' }}>
                  {["District", "Min Lot", "Front", "Side (1st/2nd)", "Rear (1st/2nd)", "Max Coverage"].map((h) => (
                    <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {zoningDistricts.map((z) => (
                  <tr key={z.district} style={{ borderBottom: '1px solid var(--border-row)' }}>
                    <td className="px-3 py-2 text-xs font-semibold text-gray-200">{z.district}</td>
                    <td className="px-3 py-2 text-xs font-mono text-gray-300">{z.minLot}</td>
                    <td className="px-3 py-2 text-xs font-mono text-gray-300">{z.front}</td>
                    <td className="px-3 py-2 text-xs font-mono text-gray-300">{z.side}</td>
                    <td className="px-3 py-2 text-xs font-mono text-gray-300">{z.rear}</td>
                    <td className="px-3 py-2 text-xs font-mono text-gray-300">{z.coverage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </InfoBentoCard>

      {/* Permit Phase Checklists */}
      <div className="space-y-3">
        {phases.map((phase) => {
          const Icon = iconMap[phase.icon] || Compass;
          const isOpen = expanded.has(phase.id);
          const phaseComplete = phase.items.filter((i) => checklist[i.id]).length;
          const phaseTotal = phase.items.length;
          const phasePct = phaseTotal > 0 ? Math.round((phaseComplete / phaseTotal) * 100) : 0;

          return (
            <InfoBentoCard
              key={phase.id}
              cornerGlow
              frostedGradient
              accent={phase.accent}
            >
              <div
                className="flex items-center justify-between cursor-pointer -m-4 p-4"
                onClick={(e) => toggleExpand(phase.id, e)}
              >
                <div className="flex items-center gap-3">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                  <div className={`p-1.5 rounded-md text-${phase.accent}-400 bg-${phase.accent}-900/40`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-sm font-bold uppercase tracking-wider text-gray-200">{phase.name}</span>
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      {phaseComplete}/{phaseTotal} items completed
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-20 rounded-full h-1" style={{ background: 'var(--bg-progress-track)' }}>
                    <div
                      className={`h-1 rounded-full bg-${phase.accent}-500/60`}
                      style={{ width: `${phasePct}%` }}
                    />
                  </div>
                  {phasePct === 100 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">Done</span>
                  )}
                </div>
              </div>

              {isOpen && (
                <div className="mt-4 pt-3 space-y-1" style={{ borderTop: '1px solid var(--border-row)' }}>
                  {phase.items.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-start gap-2.5 p-2 rounded-lg cursor-pointer group"
                      style={{ background: checklist[item.id] ? 'var(--bg-row-hover)' : 'transparent' }}
                      onClick={() => toggleItem(item.id)}
                    >
                      <div className="mt-0.5 flex-shrink-0">
                        {checklist[item.id] ? (
                          <Check className="w-3.5 h-3.5 text-emerald-400" />
                        ) : (
                          <Circle className="w-3.5 h-3.5 text-gray-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs ${checklist[item.id] ? "line-through text-gray-600" : "text-gray-300"}`}>
                          {item.text}
                        </p>
                        {item.note && (
                          <p className="text-[11px] text-gray-500 mt-0.5">{item.note}</p>
                        )}
                        <StepBadges item={item} reviewPath={reviewPath} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </InfoBentoCard>
          );
        })}
      </div>
    </div>
  );
}
